import React, { useState } from 'react';
import { Receipt, Zap, Crown, Building, BadgeCheck, ArrowRight } from 'lucide-react';
import NumberFlow from '@number-flow/react';

interface Feature {
  included: boolean;
  text: string;
}

interface PricingPlan {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  price: string;
  period: string;
  originalPrice?: string;
  features: Feature[];
  ctaText: string;
  popular?: boolean;
}

interface PricingCardProps {
  plan: PricingPlan;
  onSelect: () => void;
  highlighted?: boolean;
}

const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  onSelect,
  highlighted = false,
}) => {
  // Extract numeric price, handling "ab XX€" format
  const priceMatch = plan.price.match(/\d+/);
  const isNumericPrice = priceMatch !== null && plan.price !== '0€' && !plan.price.includes('auf Anfrage');
  const numericPrice = priceMatch ? parseInt(priceMatch[0]) : 0;
  const numericOriginalPrice = plan.originalPrice ? parseInt(plan.originalPrice.replace('€', '')) : undefined;

  // Special styling for Growth plan (now "verlag")
  const isGrowth = plan.id === 'growth' || plan.id === 'verlag';

  return (
    <div
      className={`relative flex flex-col h-full rounded-2xl transition-all duration-300
      ${highlighted
        ? 'bg-gray-900 text-white shadow-2xl transform scale-105'
        : isGrowth
        ? 'bg-gradient-to-br from-purple-600 to-purple-700 text-white shadow-xl transform scale-105'
        : plan.popular
        ? 'bg-white border-2 border-blue-500 shadow-lg'
        : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md'
      }`}
    >
      {/* Background pattern for highlighted card */}
      {(highlighted || isGrowth) && (
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff1a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff1a_1px,transparent_1px)] bg-[size:45px_45px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)] rounded-2xl" />
      )}

      {/* Popular background gradient */}
      {plan.popular && !highlighted && !isGrowth && (
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(59,130,246,0.05),rgba(255,255,255,0))] rounded-2xl" />
      )}


      <div className="relative p-5 flex flex-col h-full">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold mb-2">{plan.title}</h3>
          <p className={`text-sm h-9 flex items-center justify-center ${highlighted || isGrowth ? 'text-gray-300' : 'text-gray-600'}`}>
            {plan.description}
          </p>

          <div className="mt-5 mb-2">
            {isNumericPrice ? (
              <>
                {plan.price.includes('ab') && <span className="text-xl font-normal mr-1">ab </span>}
                <NumberFlow
                  value={numericPrice}
                  format={{ style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }}
                  className="text-4xl font-bold inline-block"
                />
                <span className={`ml-1 ${highlighted || isGrowth ? 'text-gray-300' : 'text-gray-600'}`}>{plan.period}</span>
              </>
            ) : (
              <>
                <span className="text-4xl font-bold">{plan.price}</span>
                <span className={`ml-1 ${highlighted || isGrowth ? 'text-gray-300' : 'text-gray-600'}`}>{plan.period}</span>
              </>
            )}
          </div>

          <div className="h-5">
            {plan.originalPrice && numericOriginalPrice && (
              <p className={`text-sm ${highlighted || isGrowth ? 'text-gray-400' : 'text-gray-500'}`}>
                Statt{' '}
                <NumberFlow
                  value={numericOriginalPrice}
                  format={{ style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }}
                  className="inline-block"
                />
                {plan.period}
              </p>
            )}
          </div>
        </div>

        <div className="flex-grow">
          <ul className="space-y-2.5">
            {plan.features.filter(feature => feature.included).map((feature, index) => (
              <li key={index} className="flex items-center">
                <BadgeCheck className={`h-4 w-4 mr-2.5 flex-shrink-0 ${highlighted || isGrowth ? 'text-green-400' : 'text-green-500'}`} />
                <span className={`text-sm ${highlighted || isGrowth ? 'text-gray-100' : 'text-gray-700'}`}>
                  {feature.text}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6">
          <button
            onClick={onSelect}
            className={`w-full py-2.5 px-4 rounded-lg transition-all duration-200 font-medium flex items-center justify-center group
            ${highlighted
              ? 'bg-white text-gray-900 hover:bg-gray-100'
              : isGrowth
              ? 'bg-white text-purple-700 hover:bg-purple-50'
              : plan.popular
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
          >
            {plan.ctaText}
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

const PricingTab: React.FC<{
  text: string;
  selected: boolean;
  onClick: () => void;
  discount?: boolean;
}> = ({ text, selected, onClick, discount }) => {
  return (
    <button
      onClick={onClick}
      className={`relative px-6 py-2.5 text-sm font-semibold capitalize transition-all duration-200
        ${selected ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}
        ${discount ? 'flex items-center gap-2' : ''}
      `}
    >
      <span className="relative z-10">{text}</span>
      {selected && (
        <span className="absolute inset-0 bg-white rounded-full shadow-sm" />
      )}
      {discount && (
        <span className={`relative z-10 text-xs px-2 py-0.5 rounded-full font-medium
          ${selected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}
        `}>
          20% sparen
        </span>
      )}
    </button>
  );
};

interface PricingSectionProps {
  onPlanSelect: (planId: string) => void;
  showEnterprise?: boolean;
  showEnterpriseOnly?: boolean;
  trialExpired?: boolean;
}

export const PricingSection: React.FC<PricingSectionProps> = ({
  onPlanSelect,
  showEnterprise = true,
  showEnterpriseOnly = false,
  trialExpired = false
}) => {
  const [isAnnual, setIsAnnual] = useState(true);

  // Store billing period in sessionStorage when it changes
  React.useEffect(() => {
    sessionStorage.setItem('pricingBillingPeriod', isAnnual ? 'annual' : 'monthly');
  }, [isAnnual]);

  const pricingPlans: PricingPlan[] = [
    {
      id: 'starter',
      title: 'START',
      icon: <></>,
      description: 'pro Titel',
      price: isAnnual ? 'ab 29€' : 'ab 35€',
      period: '/Monat je Titel',
      originalPrice: undefined,
      features: [
        { included: true, text: 'Links steuern (Basis): Ziel später ändern' },
        { included: true, text: '1 Seite hinter dem QR (z. B. Reihen-Seite)' },
        { included: true, text: 'bis 10 000 Scans/Monat inklusive' },
        { included: true, text: '1 Nutzer:in' },
      ],
      ctaText: trialExpired ? 'Jetzt starten' : '7 Tage kostenlos testen',
      popular: false,
    },
    {
      id: 'growth',
      title: 'VERLAG',
      icon: <></>,
      description: 'mehrere Titel',
      price: isAnnual ? 'ab 299€' : 'ab 359€',
      period: '/Monat',
      originalPrice: undefined,
      features: [
        { included: true, text: 'Links steuern (Erweitert): Land/Gerät/Auflage' },
        { included: true, text: 'Seiten bauen: beliebig viele' },
        { included: true, text: 'bis 200 000 Scans/Monat inklusive' },
        { included: true, text: '5 Nutzer:innen' },
        { included: true, text: 'Vorschläge (KI)' },
        { included: true, text: 'Auftragsverarbeitung nach DSGVO' },
      ],
      ctaText: trialExpired ? 'Jetzt starten' : '7 Tage kostenlos testen',
      popular: true,
    },
    {
      id: 'business',
      title: 'ENTERPRISE',
      icon: <></>,
      description: 'auf Anfrage',
      price: 'auf Anfrage',
      period: '',
      originalPrice: undefined,
      features: [
        { included: true, text: 'Firmen-Login & feste Reaktionszeiten' },
        { included: true, text: 'sehr hohe Scan-Mengen' },
        { included: true, text: 'Workflows & Schnittstellen' },
        { included: true, text: 'Datenschutz/Security-Prüfung' },
      ],
      ctaText: 'Kontakt aufnehmen',
      popular: false,
    },
  ];

  // If showing enterprise only, render just the enterprise section
  if (showEnterpriseOnly) {
    return (
      <div className="w-full text-white relative">
        {/* Background pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:50px_50px]" />

        <div className="relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text">
              Enterprise-Lösung
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Maßgeschneiderte Lösungen für große Verlage und Unternehmen
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <PricingCard
              plan={pricingPlans[2]}
              onSelect={() => onPlanSelect('enterprise')}
              highlighted={true}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="text-center mb-12">
        <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-gray-900">
          Transparente Preise
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
          Wählen Sie den Plan, der am besten zu Ihnen passt. Alle Pläne beinhalten eine 7-tägige kostenlose Testphase.
        </p>

        {/* Annual/Monthly Toggle */}
        <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
          <PricingTab
            text="Jährlich"
            selected={isAnnual}
            onClick={() => setIsAnnual(true)}
            discount={true}
          />
          <PricingTab
            text="Monatlich"
            selected={!isAnnual}
            onClick={() => setIsAnnual(false)}
          />
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-12">
        {pricingPlans.filter(plan => showEnterprise || plan.id !== 'enterprise').map((plan) => (
          <PricingCard
            key={plan.id}
            plan={plan}
            onSelect={() => onPlanSelect(plan.id)}
          />
        ))}
      </div>

      {/* Pilot Info */}
      <div className="text-center mt-8 bg-blue-50 rounded-xl p-6 max-w-3xl mx-auto">
        <h4 className="text-lg font-bold text-blue-900 mb-2">
          Pilot: 2 Titel · 90 Tage · Setup 0 €
        </h4>
        <p className="text-sm text-blue-800">
          Wir schauen gemeinsam auf Scans pro 1 000 Bücher, Klick-Anteile zu Video/Shop und Zeit bis Update. Entscheidung nach Daten.
        </p>
        <p className="text-xs text-gray-600 mt-2">
          Pilot-Gebühren werden auf ein Jahresabo angerechnet.
        </p>
      </div>
    </div>
  );
};
